import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { useLatest } from "hooks/useLatest";
import { floorPlanLayoutKeys } from "../useFloorPlanLayout";
import type { FloorPlanLayout, FloorPlanLayoutRoom } from "../../utils/types";
import type {
  CreateRoomsArgs,
  FloorPlanRoomRow,
  MergedRoomRow,
  MergeRoomsArgs,
  RenameRoomArgs,
  RoomsUpdater,
  UpdateRoomGeometryArgs,
  UseFloorPlanRoomsCrudArgs,
  UseFloorPlanRoomsCrudResult,
} from "./types";

export const useFloorPlanRoomsCrud = ({
  floorPlanId,
  rooms,
}: UseFloorPlanRoomsCrudArgs): UseFloorPlanRoomsCrudResult => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isCreatingRooms, setIsCreatingRooms] = useState(false);

  const floorPlanIdLatest = useLatest(floorPlanId);

  const setCachedRooms = useCallback(
    (targetFloorPlanId: number, update: RoomsUpdater) => {
      // Returning undefined leaves an uncached layout alone instead of faking an empty one.
      queryClient.setQueryData<FloorPlanLayout>(
        floorPlanLayoutKeys.detail(targetFloorPlanId),
        (current) => current && { ...current, rooms: update(current.rooms) },
      );
    },
    [queryClient],
  );

  const createRooms = useCallback(
    async ({ pageId, pageIndex, rooms: newRooms }: CreateRoomsArgs) => {
      const targetFloorPlanId = floorPlanIdLatest.current;
      if (targetFloorPlanId === undefined) return null;

      setIsCreatingRooms(true);
      try {
        const created: FloorPlanRoomRow[] =
          await api.bulkCreateQuantitySurveyorFloorPlanRooms({
            pageId,
            rooms: newRooms,
          });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROOMS_CREATED, {
          roomCount: created.length,
        });
        // A hand-drawn room has no zone and no printed area.
        const createdRooms: FloorPlanLayoutRoom[] = created.map((row) => ({
          id: row.id,
          pageIndex,
          name: row.name,
          // A typed name is stored in both languages, as the server does.
          nameDe: row.name,
          points: row.points,
          zoneId: null,
          statedAreaM2: null,
        }));
        setCachedRooms(targetFloorPlanId, (current) => [
          ...current,
          ...createdRooms,
        ]);
        return createdRooms;
      } catch {
        toast.error(t("smartSurveyor.rooms.createFailed"));
        return null;
      } finally {
        setIsCreatingRooms(false);
      }
    },
    [floorPlanIdLatest, setCachedRooms, t],
  );

  const mergeRooms = useCallback(
    async ({ pageId, pageIndex, roomIds, name, points }: MergeRoomsArgs) => {
      const targetFloorPlanId = floorPlanIdLatest.current;
      if (targetFloorPlanId === undefined) return null;

      try {
        const row: MergedRoomRow =
          await api.mergeQuantitySurveyorFloorPlanRooms({
            pageId,
            roomIds,
            name,
            points,
          });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROOMS_MERGED, {
          roomCount: roomIds.length,
        });
        const mergedRoom: FloorPlanLayoutRoom = {
          id: row.id,
          pageIndex,
          name: row.name,
          nameDe: row.name,
          points: row.points,
          zoneId: row.zoneId,
          statedAreaM2: null,
        };
        setCachedRooms(targetFloorPlanId, (current) => [
          ...current.filter((room) => !roomIds.includes(room.id)),
          mergedRoom,
        ]);
        return mergedRoom;
      } catch {
        toast.error(t("smartSurveyor.rooms.mergeFailed"));
        return null;
      }
    },
    [floorPlanIdLatest, setCachedRooms, t],
  );

  const updateRoomGeometry = useCallback(
    async ({ roomId, points }: UpdateRoomGeometryArgs) => {
      const targetFloorPlanId = floorPlanIdLatest.current;
      if (targetFloorPlanId === undefined) return false;
      try {
        await api.updateQuantitySurveyorFloorPlanRoom(roomId, { points });
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROOM_SHAPE_EDITED);
        setCachedRooms(targetFloorPlanId, (current) =>
          current.map((room) =>
            room.id === roomId ? { ...room, points } : room,
          ),
        );
        return true;
      } catch {
        toast.error(t("smartSurveyor.rooms.updateFailed"));
        return false;
      }
    },
    [floorPlanIdLatest, setCachedRooms, t],
  );

  const renameRoom = useCallback(
    ({ roomId, name }: RenameRoomArgs) => {
      if (floorPlanId === undefined) return;
      // `rooms` hold the names the user sees, so "unchanged" means unchanged to them.
      const room = rooms.find((candidate) => candidate.id === roomId);
      if (!room || room.name === name) return;

      // The cache holds the stored names, which a failed rename must put back.
      const storedRoom = queryClient
        .getQueryData<FloorPlanLayout>(floorPlanLayoutKeys.detail(floorPlanId))
        ?.rooms.find((candidate) => candidate.id === roomId);

      const setNames = (nextName: string, nextNameDe: string | null) =>
        setCachedRooms(floorPlanId, (current) =>
          current.map((candidate) =>
            candidate.id === roomId
              ? { ...candidate, name: nextName, nameDe: nextNameDe }
              : candidate,
          ),
        );

      // A typed name is stored in both languages, as the server does.
      setNames(name, name);
      api
        .updateQuantitySurveyorFloorPlanRoom(roomId, { name })
        .then(() => trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROOM_RENAMED))
        .catch(() => {
          setNames(storedRoom?.name ?? room.name, storedRoom?.nameDe ?? null);
          toast.error(t("smartSurveyor.rooms.updateFailed"));
        });
    },
    [floorPlanId, rooms, queryClient, setCachedRooms, t],
  );

  const deleteRoom = useCallback(
    async (roomId: number) => {
      const targetFloorPlanId = floorPlanIdLatest.current;
      if (targetFloorPlanId === undefined) return false;
      try {
        await api.deleteQuantitySurveyorFloorPlanRoom(roomId);
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROOM_DELETED);
        setCachedRooms(targetFloorPlanId, (current) =>
          current.filter((room) => room.id !== roomId),
        );
        return true;
      } catch {
        toast.error(t("smartSurveyor.rooms.deleteFailed"));
        return false;
      }
    },
    [floorPlanIdLatest, setCachedRooms, t],
  );

  return {
    isCreatingRooms,
    createRooms,
    mergeRooms,
    updateRoomGeometry,
    renameRoom,
    deleteRoom,
  };
};
